import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { Feature, Map } from 'ol';
import ClusterSource from 'ol/source/Cluster';
import { AnimatedClusterLayerBuilder } from '@datum-map';
import VectorSource from 'ol/source/Vector';
import { Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { bbox } from 'ol/loadingstrategy';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import image from '@assets/images/news-layer.svg';
import { addBadgeStyle, BadgeOptions } from '@utils/badge';
import { INewsLayerController } from './types';
import { DATUM_MAP_CONFIG } from '../datumMapConfig';
import {
  FeatureCollectionSourceLoaderDecorator,
  LoadFeatureCollectionCallback,
} from '../SourceLoaderDecorator';

type Mode = 'editing' | 'viewing';

interface NewsLayerOptions {
  mode: Mode;
}

export class NewsLayerController implements INewsLayerController {
  @observable private _mode: 'editing' | 'viewing';
  @observable private _isVisible = false;
  @observable private _count = undefined;
  @observable private _requestsInProgress: number = 0;
  @observable private _error: string | undefined = undefined;

  private _emptyStyle = new Style({});
  private _source = new VectorSource<Feature<Point>>({ strategy: bbox });
  // TODO: any
  private _animatedLayerSelect: any;
  private _animatedLayer: VectorLayer;
  private _defaultLayer: VectorLayer;
  private _zIndex = 1000;
  private _singleFeatureStyle = new Style({
    image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.03 }),
  });
  private _clusterStyle = new Style({
    image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.04 }),
  });
  private _badgeOptions: BadgeOptions = {
    displacement: [15, 15],
    backgroundColor: 'rgb(64, 152, 87)',
    color: '#fff',
  };

  constructor(
    private _map: Map,
    private _loadFeatures: LoadFeatureCollectionCallback,
    { mode }: NewsLayerOptions,
  ) {
    this._mode = mode;

    const { layer: animatedLayer, select } = this._buildAnimatedLayer();

    this._animatedLayer = animatedLayer;
    this._animatedLayerSelect = select;

    this._defaultLayer = this._buildDefaultLayer();

    new FeatureCollectionSourceLoaderDecorator(
      this._source,
      this._map,
      this._loadFeatures,
    );

    this._bind();

    makeObservable<NewsLayerController>(this);
  }

  @computed get mode() {
    return this._mode;
  }

  set mode(value: Mode) {
    this._mode = value;

    this._setLayer();
  }

  @computed get source(): VectorSource<Feature<Point>> {
    return this._source;
  }

  @computed get layer(): VectorLayer {
    if (this._mode === 'editing') {
      return this._defaultLayer;
    }

    if (this._mode === 'viewing') {
      return this._animatedLayer;
    }

    return this._animatedLayer;
  }

  // TODO: Всегда равен 0. Эндпоинт open-news-geojson/ не возврашает count
  @computed get count() {
    return this._count;
  }

  // TODO: Всегда равно undefined.
  // Надо договориться об отображении ошибки, в том числе при условии частого изменения bbox. (10 запросов подряд => 9 первых кенселится => интерфейс моргает)
  @computed get error(): string | undefined {
    return this._error;
  }

  @computed get isLoading(): boolean {
    return this._requestsInProgress > 0;
  }

  @computed get isVisible() {
    return this._isVisible;
  }

  @action show = () => {
    if (!this._isVisible) {
      this._map.addLayer(this.layer);
    }

    this._isVisible = true;
  };

  @action hide = () => {
    this._isVisible = false;
    this._map.removeLayer(this.layer);
  };

  private _bind = () => {
    this.source.on('featuresloadstart', () => {
      runInAction(() => this._requestsInProgress++);
    });

    this.source.on(['featuresloadend', 'featuresloaderror'], () => {
      runInAction(() => this._requestsInProgress--);
    });
  };

  private _setLayer = () => {
    // Закрывает кластер перед сменой слоя. Иначе раскрывшийся кластер продолжает отображаться даже при несуществующем слое
    this._animatedLayerSelect?.clear();

    if (this._mode === 'editing') {
      this._map.removeLayer(this._animatedLayer);
      this._map.addLayer(this._defaultLayer);
    }

    if (this._mode === 'viewing') {
      this._map.removeLayer(this._defaultLayer);
      this._map.addLayer(this._animatedLayer);
    }
  };

  private _buildAnimatedLayer = () => {
    const builder = new AnimatedClusterLayerBuilder(
      DATUM_MAP_CONFIG.map.layer.cluster,
    );

    const { layer, select } = builder
      .setMap(this._map)
      .setSource(new ClusterSource({ distance: 120, source: this._source }))
      .setStyle({
        clusterStyle: addBadgeStyle(this._clusterStyle, this._badgeOptions),
        featureStyle: [this._singleFeatureStyle],
        activeClusterStyle: this._emptyStyle,
        activeFeatureStyle: this._singleFeatureStyle,
      })
      .build();

    layer.setZIndex(this._zIndex);

    return { layer, select };
  };

  private _buildDefaultLayer = () => {
    const layer = new VectorLayer({ source: this._source });

    layer.setZIndex(this._zIndex);
    layer.setStyle(this._singleFeatureStyle);

    return layer;
  };
}
